/**
 * Parts shared by the case content editors (CaseContentEditors.tsx).
 */
import type { ChangeEvent } from 'react';

export interface CaseInputExpect {
  input: unknown;
  expect: unknown;
}

/**
 * An editor starts from `initial` and keeps its own draft; it reports every change, or
 * `null` while the draft cannot be sent (JSON that does not parse).
 */
export interface ContentEditorProps {
  initial: CaseInputExpect | null;
  /** The suite's case options from the API (EvalSuiteInfo.caseOptions). */
  options: Record<string, string[]>;
  disabled: boolean;
  onChange: (value: CaseInputExpect | null) => void;
}

export const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

export const strings = (value: unknown): string[] => (Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []);

/** One item per line, blank lines dropped. */
export const toList = (text: string): string[] =>
  text
    .split('\n')
    .map((item) => item.trim())
    .filter((item) => item !== '');

export const fieldClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50';

export const FieldLabel = ({ title, hint }: { title: string; hint?: string }) => (
  <span className="block mb-1">
    <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">{title}</span>
    {hint && <span className="ml-2 text-xs text-gray-500">{hint}</span>}
  </span>
);

/** A list edited as text, one item per line; the box grows with the list. */
export const ListField = ({ title, hint, value, disabled, onChange }: { title: string; hint: string; value: string; disabled: boolean; onChange: (value: string) => void }) => (
  <label className="block">
    <FieldLabel title={title} hint={hint} />
    <textarea
      value={value}
      disabled={disabled}
      rows={Math.max(3, value.split('\n').length + 1)}
      onChange={(event: ChangeEvent<HTMLTextAreaElement>) => onChange(event.target.value)}
      className={fieldClass}
    />
  </label>
);
